import { memo, useCallback, useMemo } from 'react';

import { type Board } from '@air/api/types';

import { getItemPickerBoardsListParams } from '~/components/ItemPicker/getItemPickerBoardsListParams';
import {
  ItemPickerGalleryView,
  type ItemPickerGalleryViewBoardsProps,
  type ItemPickerGalleryViewClipsProps,
} from '~/components/ItemPicker/ItemPickerGalleryView';
import { type ItemPickerNavView } from '~/components/ItemPicker/itemPickerTypes';
import { usePrivateBoardsList } from '~/components/v2/hooks/queries/usePrivateBoardsList';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

type ItemPickerBoardsListQueryData = {
  pages: Array<{ data: Board[]; total?: number }>;
};

type ItemPickerBoardsFromQuery = {
  boards: Board[];
  boardsTotal: number;
};

const EMPTY_ITEM_PICKER_BOARDS_FROM_QUERY: ItemPickerBoardsFromQuery = {
  boards: [],
  boardsTotal: 0,
};

/**
 * TanStack's skipToken union types `data` as always present; the first fetch is still empty.
 * Taking `| undefined` here keeps the runtime guard without a `?.` biome warning at the call site.
 */
const getItemPickerBoardsFromQueryData = (
  data: ItemPickerBoardsListQueryData | undefined,
): ItemPickerBoardsFromQuery => {
  if (!data) {
    return EMPTY_ITEM_PICKER_BOARDS_FROM_QUERY;
  }

  return {
    boards: data.pages.flatMap((page) => page.data),
    boardsTotal: data.pages[data.pages.length - 1]?.total ?? 0,
  };
};

export type ItemPickerBoardsSectionProps = ItemPickerGalleryViewClipsProps &
  Pick<
    ItemPickerGalleryViewBoardsProps,
    'areBoardsSelectable' | 'onBoardClick' | 'onBoardDoubleClick' | 'registerBoardElement' | 'selectedBoardIds'
  > & {
    navView: ItemPickerNavView;
  };

/**
 * Binds the picker's current nav view to Boards.list and forwards the clips-side props so boards
 * and assets render in one virtualized stream. Search, favorites, recents, libraries, and
 * in-board browsing all share this query so a typed title always reaches the boards endpoint.
 */
export const ItemPickerBoardsSection = memo(
  ({
    areBoardsSelectable,
    navView,
    onBoardClick,
    onBoardDoubleClick,
    registerBoardElement,
    search,
    selectedBoardIds,
    ...clipsProps
  }: ItemPickerBoardsSectionProps) => {
    const { currentWorkspace } = useCurrentWorkspace();
    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, status } = usePrivateBoardsList(
      getItemPickerBoardsListParams({
        navView,
        search,
        workspaceId: currentWorkspace?.id,
      }),
    );
    const { boards, boardsTotal } = useMemo(() => getItemPickerBoardsFromQueryData(data), [data]);

    const handleLoadMoreBoards = useCallback(() => {
      fetchNextPage();
    }, [fetchNextPage]);

    return (
      <ItemPickerGalleryView
        {...clipsProps}
        areBoardsSelectable={areBoardsSelectable}
        boards={boards}
        boardsTotal={boardsTotal}
        hasMoreBoards={!!hasNextPage}
        isLoadingBoards={status === 'pending' || isFetchingNextPage}
        onBoardClick={onBoardClick}
        onBoardDoubleClick={onBoardDoubleClick}
        onLoadMoreBoards={handleLoadMoreBoards}
        registerBoardElement={registerBoardElement}
        search={search}
        selectedBoardIds={selectedBoardIds}
      />
    );
  },
);

ItemPickerBoardsSection.displayName = 'ItemPickerBoardsSection';
